import { type Metadata } from 'next';
import { omit } from 'ramda';

import {
  type FullParams,
  type PageType,
  getCanonicalUrlArgs,
  getPageUrl,
  internalToRouteMapping,
  isPageIndexable,
  splitUrlRegion,
} from '@/app/urls/url-mapping';
import { regionToMainCountry } from '@/lib/utils';
import { type Language } from '@/lib/utils/language';
import { type Region, type RegionCountry, type UrlRegion } from '@/types';

export type SeoMetadata = {
  seo: {
    description: string;
    title: string;
  } | null;
};

export type Alternates = Partial<
  Record<`${Language}-${RegionCountry}` | 'x-default', string>
>;

export const getAlternateLocale = (
  region: Region,
  language: Language
): Exclude<keyof Alternates, 'x-default'> =>
  `${language}-${regionToMainCountry(region)}`;

/**
 * Adds Google's `x-default` hreflang entry.
 *
 * Always points at the US/EN equivalent of the current page when that
 * alternate exists; otherwise falls back to the US homepage.
 *
 * @example
 * withDefaultHreflang({
 *   'es-es': 'https://www.ironhack.com/es/desarrollo-web',
 *   'en-us': 'https://www.ironhack.com/us/web-development',
 * })
 * // → also includes 'x-default': 'https://www.ironhack.com/us/web-development'
 */
export const withDefaultHreflang = (languages: Alternates): Alternates => ({
  ...languages,
  'x-default':
    languages['en-us'] ??
    getPageUrl('home', { language: 'en', region: 'us' }, { withOrigin: true }),
});

// Removes params that we don't want when creating alternate region URLs:
// - pagination because that page may not exist in the other region
// - slug because the slug might be different in the other region
export const sanitizeAlternateParams = omit(['pagination', 'slug']);

export const getAlternateMetaTags = async (
  pageType: PageType,
  params: FullParams
): Promise<Pick<Metadata, 'alternates'>> => {
  if (!isPageIndexable(pageType, params)) return {};

  // Occasionally the canonical URL is changed for SEO purposes
  const [canonType, canonParams] = await getCanonicalUrlArgs(pageType, params);
  let canonical =
    getPageUrl(canonType, canonParams, { withOrigin: true }) ?? '';

  const experimentIndexInUrl = canonical.indexOf('/e/');
  if (experimentIndexInUrl !== -1) {
    canonical = canonical.slice(0, experimentIndexInUrl);
  }

  const pageRoutes = internalToRouteMapping[pageType];

  // Don't include alternate language links if the canonical URL is changed
  if (!pageRoutes || canonType !== pageType)
    return { alternates: { canonical } };

  const paramsWithoutPagination = sanitizeAlternateParams(params);
  const pageRegions = Object.keys(pageRoutes) as UrlRegion[];

  const languages = pageRegions.reduce<Alternates>((regionAcc, urlRegion) => {
    const { region, language } = splitUrlRegion(urlRegion);

    const url = getPageUrl(
      pageType,
      { ...paramsWithoutPagination, language, region },
      { withOrigin: true }
    );

    if (
      !url ||
      !isPageIndexable(pageType, { language, region }) ||
      region === 'ww'
    )
      return regionAcc;

    const locale = getAlternateLocale(region, language);
    return { ...regionAcc, [locale]: url };
  }, {});

  return {
    alternates: { canonical, languages: withDefaultHreflang(languages) },
  };
};

export const getRobotsMetaTags = (
  pageType: PageType,
  params: FullParams
): Pick<Metadata, 'robots'> =>
  isPageIndexable(pageType, params)
    ? {}
    : {
        robots: {
          index: false,
          ...(params.region === 'ww' && { follow: true }),
        },
      };
